/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import DashboardAppShell from '../components/DashboardAppShell';
import { useApp } from '../context/AppContext';
import { useNovaAI } from '../hooks/useNovaAI';
import { NOVA_SUGGESTIONS, getNovaFirstMessage } from '../data';
import NovaAvatarImg from '../components/NovaAvatarImg';

const FONT_SERIF = "'Playfair Display', Georgia, serif";
const FONT_BODY = "'Plus Jakarta Sans', -apple-system, sans-serif";

const CHAPTER_CONFIG = {
  ttc: { label: 'Trying to Conceive' },
  fertility: { label: 'Fertility' },
  ivf: { label: 'Fertility' },
  pregnancy: { label: 'Pregnancy' },
  postpartum: { label: 'Motherhood' },
  cycle: { label: 'Trying to Conceive' },
  menopause: { label: 'Motherhood' },
  surrogacy: { label: 'Surrogacy' },
  baby: { label: 'Motherhood' },
  loss: { label: 'Pregnancy Loss' },
};

export default function NovaChat() {
  const { user, messages, addMessage, plan, messagesRemaining, novaDraft, setNovaDraft } = useApp();
  const { sendToNova, isLoading } = useNovaAI();
  const [input, setInput] = useState('');
  const bottomRef = useRef(null);
  const textareaRef = useRef(null);

  const chapter = (user?.journeyType || 'default').toLowerCase();
  const chapterKey = chapter === 'ivf' ? 'fertility' : chapter === 'cycle' || chapter === 'first-chapter' ? 'ttc' : chapter;
  const chapterInfo = CHAPTER_CONFIG[chapterKey] || CHAPTER_CONFIG.fertility;
  const suggestions = useMemo(() => (NOVA_SUGGESTIONS[chapter] || NOVA_SUGGESTIONS.fertility || NOVA_SUGGESTIONS.default).slice(0, 3), [chapter]);

  const firstMessageContent = useMemo(() => {
    if (!user || messages.length !== 1 || messages[0]?.role !== 'assistant') return null;
    const firstName = user.first_name || user.name?.split(/\s+/)[0] || user.name;
    return getNovaFirstMessage(firstName, user.journeyType, user.journey_detail);
  }, [user, messages]);

  const displayMessages = useMemo(() => {
    if (!firstMessageContent || messages.length === 0) return messages;
    return [{ role: 'assistant', content: firstMessageContent }, ...messages.slice(1)];
  }, [messages, firstMessageContent]);

  const showChips = displayMessages.length <= 1;

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  useEffect(() => {
    if (!novaDraft || isLoading) return;
    const t = setTimeout(() => {
      send(novaDraft);
      setNovaDraft('');
    }, 0);
    return () => clearTimeout(t);
  }, [novaDraft]);

  async function send(text) {
    const msg = text || input.trim();
    if (!msg || isLoading) return;
    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    addMessage({ role: 'user', content: msg });
    const history = [...messages, { role: 'user', content: msg }];
    const reply = await sendToNova(history, user);
    addMessage({ role: 'assistant', content: reply });
  }

  function handleKey(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  }

  function autoResize(e) {
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
  }

  const atLimit = plan === 'free' && messagesRemaining <= 0;

  return (
    <DashboardAppShell fontFamily={FONT_BODY} mainStyle={{ display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}>
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, minWidth: 0 }}>
        {/* Chat header */}
        <header style={{ background: 'white', borderBottom: '1px solid var(--border)', padding: '16px 24px', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ position: 'relative' }}>
              <NovaAvatarImg size={44} alt="" />
              <div style={{
                position: 'absolute',
                bottom: 2,
                right: 2,
                width: 12,
                height: 12,
                borderRadius: '50%',
                background: 'var(--sage)',
                border: '2px solid white',
              }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>Nova</div>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>Your OrivonAI companion · always here</div>
            </div>
            <span style={{
              fontSize: 11,
              fontWeight: 600,
              padding: '6px 12px',
              borderRadius: 999,
              background: '#FBEAF0',
              color: '#993556',
            }}>
              {chapterInfo?.label || 'Your chapter'}
            </span>
          </div>
        </header>

        {/* Messages */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>
          {displayMessages.map((msg, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                alignItems: 'flex-end',
              }}
            >
              {msg.role === 'assistant' ? (
                <div style={{ maxWidth: '85%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 6 }}>NOVA</div>
                  <div style={{
                    padding: '14px 18px',
                    borderRadius: 12,
                    borderLeft: '4px solid var(--pink)',
                    background: 'white',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                    fontSize: 14,
                    lineHeight: 1.65,
                    color: 'var(--text)',
                    whiteSpace: 'pre-wrap',
                  }}>
                    {msg.content}
                  </div>
                </div>
              ) : (
                <div style={{
                  maxWidth: '78%',
                  padding: '12px 16px',
                  borderRadius: 12,
                  borderBottomRightRadius: 4,
                  background: 'var(--sand-2)',
                  fontSize: 14,
                  lineHeight: 1.6,
                  color: 'var(--text)',
                  whiteSpace: 'pre-wrap',
                }}>
                  {msg.content}
                </div>
              )}
            </div>
          ))}

          {isLoading && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 6 }}>NOVA</div>
              <div style={{ padding: '14px 18px', borderLeft: '4px solid var(--pink)', background: 'white', borderRadius: 12, display: 'flex', gap: 4 }}>
                {[0, 1, 2].map((j) => (
                  <div key={j} style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--pink-m)' }} />
                ))}
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Suggestion chips — only below first message */}
        {showChips && (
          <div style={{ padding: '0 24px 16px', flexShrink: 0 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {suggestions.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => send(s)}
                  style={{
                    fontSize: 11,
                    fontWeight: 400,
                    padding: '8px 14px',
                    borderRadius: 999,
                    border: '1px solid var(--pink-m)',
                    color: '#111111',
                    background: '#FBEAF0',
                    cursor: 'pointer',
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Free limit notice */}
        {plan === 'free' && (
          <div style={{
            padding: '8px 24px',
            background: '#FBEAF0',
            borderTop: '1px solid #F4C0D1',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: 11,
            color: 'var(--pink-d)',
          }}>
            <span>{messagesRemaining} of 20 free messages remaining this month</span>
            <span style={{ fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>Upgrade to Bloom for unlimited →</span>
          </div>
        )}

        {/* Input */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid var(--border)',
          background: 'white',
          display: 'flex',
          gap: 10,
          alignItems: 'flex-end',
          flexShrink: 0,
        }}>
          <button
            type="button"
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              border: '1px solid var(--border)',
              background: 'var(--sand)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
            title="Attach lab results or documents"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
            </svg>
          </button>
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => { setInput(e.target.value); autoResize(e); }}
            onKeyDown={handleKey}
            placeholder={atLimit ? 'Upgrade to Bloom to keep chatting...' : 'Ask Nova anything...'}
            disabled={atLimit}
            rows={1}
            style={{
              flex: 1,
              fontFamily: FONT_BODY,
              fontSize: 14,
              border: '1.5px solid var(--border)',
              borderRadius: 12,
              padding: '12px 16px',
              background: 'var(--sand)',
              color: 'var(--text)',
              outline: 'none',
              resize: 'none',
              minHeight: 44,
              maxHeight: 120,
              lineHeight: 1.5,
            }}
          />
          <button
            type="button"
            onClick={() => send()}
            disabled={!input.trim() || isLoading || atLimit}
            style={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              background: input.trim() && !isLoading && !atLimit ? 'var(--pink)' : 'var(--sand-3)',
              border: 'none',
              cursor: input.trim() && !isLoading && !atLimit ? 'pointer' : 'default',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
              <path d="M3 8h10M9 4l4 4-4 4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>

      <style>{`@keyframes dot{0%,60%,100%{transform:translateY(0)}30%{transform:translateY(-5px)}}`}</style>
    </DashboardAppShell>
  );
}
